/**
 * GeometriesListView Component
 *
 * View mode wrapper for GeometriesListCore.
 * Displays task geometries in read-only mode with zoom-to-geometry functionality.
 *
 * Features:
 * - Read-only list (no checkboxes or edit/delete actions)
 * - Zoom to geometry on map (via Zustand map.zoomTo action)
 * - Highlight selected geometry (local state)
 * - Click item to highlight
 * - Click zoom button (IconFocus2) to zoom to geometry
 *
 * Architecture:
 * - Reuses GeometriesListCore with mode="view"
 * - Zustand atomic selector for map.zoomTo (0 re-renders)
 * - Local useState for highlighted geometry ID
 * - memo + useCallback optimization
 *
 * @module components/map/GeometriesList/view
 */

import { memo, useMemo, useCallback, useState } from 'react';
import { useGeoMarkupStore } from '../../../../store';
import { GeometriesListCore } from '../core';
import { filterVisibleGeometries } from '../utils';
import type { TaskGeometry } from '../../../../types';

/**
 * Props for GeometriesListView component
 */
export interface GeometriesListViewProps {
  /** Array of geometries to display (from task.geometries) */
  geometries: TaskGeometry[];
}

/**
 * GeometriesListView Component
 *
 * View-only wrapper that displays geometries with zoom functionality.
 * Used in TaskDetailsPanel for read-only geometry viewing.
 */
export const GeometriesListView = memo(function GeometriesListView({
  geometries,
}: GeometriesListViewProps) {
  // Local state for highlighted geometry (visual feedback only)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Zustand atomic selector - action only (0 re-renders)
  const zoomTo = useGeoMarkupStore((s) => s.map.zoomTo);

  // Filter empty/placeholder geometries - show only after adding to map
  const visibleGeometries = useMemo(() => {
    return filterVisibleGeometries(geometries);
  }, [geometries]);

  // Handle zoom to geometry on map
  const handleZoomTo = useCallback(
    (geometry: TaskGeometry) => {
      // Use geometry.calculatedCenter for zoom target
      // - Marker: Direct coordinates
      // - Line: Average of all points
      // - Shape: Centroid of bounding box
      zoomTo(geometry.calculatedCenter);
    },
    [zoomTo]
  );

  // Handle highlight (local visual state only)
  const handleHighlight = useCallback((id: string | null) => {
    setHighlightedId(id);
  }, []);

  return (
    <GeometriesListCore
      geometries={visibleGeometries}
      mode="view"
      selectedId={highlightedId}
      onSelect={handleHighlight}
      onZoomTo={handleZoomTo}
      onHighlight={handleHighlight}
      showCheckboxes={false}
      showActions={true}
    />
  );
});
